import dayjs from 'dayjs';

import { MemberDailyUtilizationResponse } from '@/modules/planner/types/dto/response/member-daily-utilization-response';

import { mapAssignmentStatusFromDTO } from '../mappers/assignment-status.mapper';
import { AssignmentStatus } from '../types/assignment-status';
import {
  DailyUtilizationResponse,
  PlannerAssignmentResponse,
  PlannerMemberResponse,
  PlannerTimeoffResponse,
} from '../types/dto';
import { TimeOffRequestStatusDTO } from '../types/dto/response/planner-timeoff-response';
import { UtilizationBasedOn } from '../types/utilization-based-on';
import { PlannerPreferences } from '../types/utilization-filters';
import { getStateChangeForDate } from './state-change/state-change';

const SATURDAY = 6;
const SUNDAY = 0;
const DATE_FORMAT = 'YYYY-MM-DD';

function buildTimeOffMinutesByDate(timeOffs: PlannerTimeoffResponse[]): Map<string, number> {
  const lookup = new Map<string, number>();
  for (const timeOff of timeOffs) {
    if (timeOff.status !== TimeOffRequestStatusDTO.APPROVED) continue;
    for (const day of timeOff.days) {
      const dateKey = dayjs(day.date).format(DATE_FORMAT);
      lookup.set(dateKey, (lookup.get(dateKey) ?? 0) + day.minutes);
    }
  }
  return lookup;
}

function applyTimeOffToAssigned(
  assigned: number,
  timeOffMinutes: number,
  capacity: number
): number {
  return Math.min(assigned + timeOffMinutes, Math.max(assigned, capacity));
}

/**
 * Normalizes any date string to YYYY-MM-DD format.
 * Backend returns full ISO ("2026-02-05T00:00:00.000Z"), but we need consistent
 * date-only keys for map lookups and downstream mappers.
 */
function normalizeDate(date: string): string {
  return dayjs(date).format(DATE_FORMAT);
}

/**
 * Builds the set of allowed assignment statuses from planner preferences.
 * Uses defaults (Confirmed, Unconfirmed, Draft) when none are specified.
 */
function buildAllowedStatuses(utilizationFilters: PlannerPreferences): Set<AssignmentStatus> {
  return new Set<AssignmentStatus>(
    utilizationFilters.assignmentStatuses ?? [
      AssignmentStatus.Confirmed,
      AssignmentStatus.Unconfirmed,
      AssignmentStatus.Draft,
    ]
  );
}

interface AssignedMinutesResult {
  /** Total assigned minutes from qualifying assignments */
  assigned: number;
  /** Whether at least one qualifying assignment was found for this date */
  hasAssignment: boolean;
}

/**
 * Calculates total assigned minutes and whether any qualifying assignments exist
 * for a specific date from a given set of assignments.
 *
 * Applies status, billable-mode, background-work, and skip-weekdays filters.
 *
 * @param assignments - Assignments to evaluate
 * @param targetDate - The date to check
 * @param allowedStatuses - Status values that count toward utilization
 * @param isBillableMode - When true, only billable assignments are counted
 */
function calculateAssignedMinutesForDate(
  assignments: PlannerAssignmentResponse[],
  targetDate: dayjs.Dayjs,
  allowedStatuses: Set<AssignmentStatus>,
  isBillableMode: boolean
): AssignedMinutesResult {
  const dayOfWeek = targetDate.day();
  let assigned = 0;
  let hasAssignment = false;

  for (const plannerAssignment of assignments) {
    const assignment = plannerAssignment.assignment;

    // Check status filter
    const appStatus = mapAssignmentStatusFromDTO(assignment.status);
    if (!allowedStatuses.has(appStatus)) continue;

    // Background work assignments are excluded from utilization calculations
    if (assignment.isBackgroundWork) continue;

    // In billable capacity mode, only billable assignments count toward utilization
    if (isBillableMode && !assignment.isBillable) continue;

    // Check if date falls within assignment range
    const assignStart = dayjs(assignment.startDate).startOf('day');
    const assignEnd = dayjs(assignment.endDate).startOf('day');

    if (targetDate.isBefore(assignStart, 'day') || targetDate.isAfter(assignEnd, 'day')) continue;

    // If assignment skips weekends, skip Saturday and Sunday
    if (assignment.doSkipWeekdays && (dayOfWeek === SATURDAY || dayOfWeek === SUNDAY)) continue;

    assigned += assignment.minutesPerDay;
    hasAssignment = true;
  }

  return { assigned, hasAssignment };
}

/**
 * Recalculates utilizationByDate for a member on specific affected dates.
 * Non-affected dates are preserved from the existing utilization array.
 *
 * @param member - The member whose utilization to recalculate
 * @param affectedDates - ISO date strings that need recalculation (YYYY-MM-DD)
 * @param utilizationBasedOn - Whether to use total or billable capacity
 * @param utilizationFilters - Which assignment statuses to include
 * @returns Updated utilizationByDate array with recalculated entries for affected dates
 */
export function recalculateUtilizationForMember(
  member: PlannerMemberResponse,
  affectedDates: string[],
  utilizationBasedOn: UtilizationBasedOn,
  utilizationFilters: PlannerPreferences
): MemberDailyUtilizationResponse[] {
  const utilMap = new Map<string, MemberDailyUtilizationResponse>();

  // Preserve existing utilization entries, normalizing date keys to YYYY-MM-DD.
  // Backend returns full ISO dates ("2026-02-05T00:00:00.000Z") but we need
  // consistent keys so recalculated entries correctly overwrite existing ones.
  for (const entry of member.utilizationByDate) {
    const normalizedDate = normalizeDate(entry.date);
    utilMap.set(normalizedDate, { ...entry, date: normalizedDate });
  }

  const allowedStatuses = buildAllowedStatuses(utilizationFilters);
  const isBillableMode = utilizationBasedOn === UtilizationBasedOn.BillableCapacity;
  const timeOffMinutesByDate = buildTimeOffMinutesByDate(member.timeOffs);

  // Recalculate each affected date
  for (const dateStr of affectedDates) {
    const targetDate = dayjs(dateStr);

    // Find capacity from state changes
    const stateChange = getStateChangeForDate(member.stateChanges, targetDate);
    if (!stateChange) {
      // No state change means user doesn't exist on this date — keep existing or skip
      continue;
    }

    const capacity = isBillableMode
      ? stateChange.workingMinutesPerDay * (stateChange.billableCapacityPercent / 100)
      : stateChange.workingMinutesPerDay;

    let { assigned } = calculateAssignedMinutesForDate(
      member.assignments,
      targetDate,
      allowedStatuses,
      isBillableMode
    );

    const timeOffMinutes = timeOffMinutesByDate.get(dateStr) ?? 0;
    assigned = applyTimeOffToAssigned(assigned, timeOffMinutes, capacity);

    // When capacity is 0, the member is at 100% utilization
    // (e.g., billable capacity mode for a non-billable member, or 0h working day)
    const percent = capacity > 0 ? Math.round((assigned / capacity) * 100) : 100;

    utilMap.set(dateStr, {
      date: dateStr,
      assigned,
      capacity,
      percent,
      onDemand: stateChange.onDemand,
    });
  }

  return Array.from(utilMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Recalculates project-level utilizationByDate for affected dates.
 *
 * Unlike department aggregation (which sums member utilizations as-is), this function
 * considers only assignments that belong to the project itself. A member's capacity is
 * included for a given date only if they have at least one qualifying assignment for
 * that project on that day — ensuring the percentage reflects actual project allocation.
 *
 * On-demand members are excluded (they have no fixed capacity to contribute to the
 * percentage calculation), consistent with how department aggregation handles them.
 *
 * @param projectId - Project to filter assignments for
 * @param members - Updated members (post-mutation patch) containing current assignments
 * @param affectedDates - ISO date strings (YYYY-MM-DD) that need recalculation
 * @param existingUtilization - Current project-level utilization (non-affected dates preserved)
 * @param utilizationBasedOn - Whether to use total or billable capacity
 * @param utilizationFilters - Assignment status and mode preferences
 */
export function recalculateProjectUtilizationForAffectedDates(
  projectId: number,
  members: PlannerMemberResponse[],
  affectedDates: string[],
  existingUtilization: DailyUtilizationResponse[],
  utilizationBasedOn: UtilizationBasedOn,
  utilizationFilters: PlannerPreferences
): DailyUtilizationResponse[] {
  const utilMap = new Map<string, DailyUtilizationResponse>();

  // Preserve existing values, normalizing keys to YYYY-MM-DD
  for (const entry of existingUtilization) {
    const key = dayjs(entry.date).format(DATE_FORMAT);
    utilMap.set(key, { ...entry, date: key });
  }

  const allowedStatuses = buildAllowedStatuses(utilizationFilters);
  const isBillableMode = utilizationBasedOn === UtilizationBasedOn.BillableCapacity;

  for (const dateStr of affectedDates) {
    const targetDate = dayjs(dateStr);
    let totalAssigned = 0;
    let totalCapacity = 0;

    for (const member of members) {
      // Only consider assignments that belong to this project
      const projectAssignments = member.assignments.filter(
        a => a.assignment.project.id === projectId
      );

      const stateChange = getStateChangeForDate(member.stateChanges, targetDate);

      // On-demand members have no fixed capacity and are excluded from the percentage calculation.
      if (stateChange && !stateChange.onDemand) {
        if (
          utilizationFilters.ignorePlaceholdersForGroup &&
          stateChange.displayStatus === 'PLACEHOLDER'
        ) {
          continue;
        }
        const { assigned, hasAssignment } = calculateAssignedMinutesForDate(
          projectAssignments,
          targetDate,
          allowedStatuses,
          isBillableMode
        );

        // Include capacity and assigned minutes only when the member has qualifying project assignments on this date.
        if (!hasAssignment) continue;

        const capacity = isBillableMode
          ? stateChange.workingMinutesPerDay * (stateChange.billableCapacityPercent / 100)
          : stateChange.workingMinutesPerDay;

        const memberTimeOff = buildTimeOffMinutesByDate(member.timeOffs);
        const timeOffMinutes = memberTimeOff.get(dateStr) ?? 0;
        totalAssigned += applyTimeOffToAssigned(assigned, timeOffMinutes, capacity);
        totalCapacity += capacity;
      }
    }

    const percent =
      totalCapacity > 0
        ? Math.round((totalAssigned / totalCapacity) * 100)
        : isBillableMode
          ? 100
          : 0;

    utilMap.set(dateStr, {
      date: dateStr,
      assigned: totalAssigned,
      capacity: totalCapacity,
      percent,
    });
  }

  return Array.from(utilMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}
